/**
 * Extracts a human-readable error message from an unknown thrown value.
 *
 * Handles the three shapes commonly produced by the codebase:
 * - Native `Error` instances (e.g. `throw new Error('...')`).
 * - Supabase / PostgREST errors — plain objects, NOT `Error` instances, with a
 *   `message` string field. Example: `{ message: 'column "foo" does not exist', code: '42703' }`.
 * - Plain string throws.
 *
 * Returns `undefined` if no usable message can be extracted, so callers can
 * fall back to a domain-specific default.
 *
 * @example
 *   const message = getErrorMessage(err) || 'Failed to save. Please try again.';
 *   addToast(message, 'error');
 */
export function getErrorMessage(err: unknown): string | undefined {
  if (err == null) return undefined;

  if (err instanceof Error && err.message) {
    return err.message;
  }

  if (typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }

  if (typeof err === 'string' && err.length > 0) return err;

  return undefined;
}

/**
 * Extracts a human-readable error message from a Supabase Edge Function invoke error.
 *
 * Supabase function errors have a layered shape:
 * 1. `err.message` or `err.error_description` (top-level string)
 * 2. `err.context.body` — a JSON string like `'{"error":"Invalid order"}'` or `'{"message":"..."}'`
 * 3. `err.body.error` — pre-parsed body in some paths
 *
 * Returns the supplied `fallback` if no message can be found at any layer.
 *
 * @example
 *   addToast(getFunctionErrorMessage(err, 'Failed to register equipment.'), 'error');
 */
export function getFunctionErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;

  const e = err as Record<string, unknown>;

  // 1) Top-level string fields
  if (typeof e.message === 'string' && e.message) return e.message;
  if (typeof e.error_description === 'string' && e.error_description) {
    return e.error_description;
  }

  // 2) Pre-parsed body
  if (e.body && typeof e.body === 'object') {
    const b = e.body as Record<string, unknown>;
    if (typeof b.error === 'string' && b.error) return b.error;
    if (typeof b.message === 'string' && b.message) return b.message;
  }

  // 3) Function error response body (often a JSON string)
  const context = e.context as { body?: unknown } | undefined;
  if (context?.body != null) {
    const raw = context.body;
    const parsed: unknown = typeof raw === 'string' ? safeJsonParse(raw) : raw;
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Record<string, unknown>;
      if (typeof p.error === 'string' && p.error) return p.error;
      if (typeof p.message === 'string' && p.message) return p.message;
    }
  }

  return fallback;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
